import isEmpty from 'lodash/isEmpty';
import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {connect} from 'react-redux';
import {window} from 'ssr-window';
import APIUtils from '@spothero/utils/api';
import DOMUtils from '@spothero/utils/dom';
import ExperimentUtils from 'utils/experiment';
import StorageUtils from '@spothero/utils/storage';
import GTMUtils from 'utils/gtm';
import ContentAPI from 'api/content';
import SegmentUtils from 'utils/segment';
import Card from 'common/card/Card';
import CloudinaryImage from 'common/image/CloudinaryImage';
import Promo from 'common/promo/Promo';
import SearchControls from 'search/controls/SearchControls';
import LookBookPark from './LookBookPark';
import HomeHero from './HomeHero';
import HomeAppPromo from './HomeAppPromo';

class Home extends Component {
    static propTypes = {
        email: PropTypes.string,
        isAdmin: PropTypes.bool,
        referrer: PropTypes.string,
        referrerAffiliate: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ])
    };
    state = {
        isLoaded: false,
        heroTitle: null,
        heroDescription: null,
        featuredImageId: null,
        horizontalPromos: null,
        verticalPromos: null,
    };

    constructor(props) {
        super(props);

        const {
            isAdmin,
        } = this.props;

        this._defaultSearchInputValue = StorageUtils.get('spothero-home-search-value');
        StorageUtils.remove('spothero-home-search-value');

        this._homepageTabbedSearchExperiment = ExperimentUtils.getVariation({
            experiment: 'homepage_tabbed_search',
            userAttributes: {
                staff: (isAdmin) ? 'true' : 'false'
            }
        });
    }

    componentDidMount() {
        const {
            referrer,
            referrerAffiliate
        } = this.props;

        this._loadData();

        SegmentUtils.page({
            name: 'Web Landing',
            properties: {
                referrer: referrer || document.referrer,
                'referrer affiliate': referrerAffiliate
            }
        });

        GTMUtils.push({
            event: 'pageview',
            page: 'home'
        });
        window.scrollTo(0, 0);
    }

    componentWillUnmount() {
        if (this._dataLoader) {
            this._dataLoader.cancel();
            this._dataLoader = null;
        }
    }

    _onFeaturedImageLoaded = evt => {
        DOMUtils.addClass(evt.target, 'Home-featured-loaded');
    }

    async _loadData() {
        let newState = {
            isLoaded: true
        };

        try {
            this._dataLoader = APIUtils.makeCancelable(
                ContentAPI.getHomepage()
            );

            const {
                data: {
                    data
                }
            } = await this._dataLoader.promise;

            newState = {
                ...newState,
                ...data
            };
        } catch (error) {
            console.log('Error loading homepage data: ', error); // eslint-disable-line no-console
        }

        if (this._dataLoader) {
            this.setState(newState);
        }

        this._dataLoader = null;
    }

    render() {
        const {
            email
        } = this.props;
        const {
            isLoaded,
            heroTitle,
            heroDescription,
            featuredImageId,
        } = this.state;
        const isSearchBarExperiment = [
            'homepage_tabbed_search',
            'tabbed_with_value_prop'
        ].some(experiment => experiment === this._homepageTabbedSearchExperiment);
        const isLookBookParkExperiment = [
            'init_homepage_with_value_prop',
            'tabbed_with_value_prop'
        ].some(experiment => experiment === this._homepageTabbedSearchExperiment);
        const contentClasses = classNames(
            {'Home-content': !isSearchBarExperiment},
        );
        const promoTypes = ['horizontal', 'vertical']
            .map(orientation => {
                const promos = this.state[`${orientation}Promos`];

                return (promos)
                    ? {orientation, promos}
                    : null;
            })
            .filter(Boolean);

        return (
            <div className="Home">
                {
                    (isSearchBarExperiment)
                        ? (
                            <HomeHero
                                initialValue={this._defaultSearchInputValue}
                            />
                        ) : (
                            <div className="Home-hero">
                                <div className="Hero-arch" />
                                {(isLoaded && heroTitle) &&
                                    <Fragment>
                                        <h1>{heroTitle}</h1>
                                        <h2>{heroDescription}</h2>
                                    </Fragment>
                                }
                            </div>
                        )
                }
                <div className={contentClasses}>
                    {
                        (!isSearchBarExperiment) && (
                            <Fragment>
                                <section className="Home-search-controls">
                                    <SearchControls
                                        defaultSearchInputValue={this._defaultSearchInputValue}
                                        showDateTimes={false}
                                        disableProTip
                                        showFindParkingButton
                                        defaultToFindParkingNearMe
                                    />
                                </section>
                                <section className="Home-container Home-featured-container">
                                    {(isLoaded && featuredImageId) &&
                                        <CloudinaryImage
                                            className="Home-featured"
                                            width={1680}
                                            height={450}
                                            cloudinaryImageId={`homepage/${featuredImageId}`}
                                            onLoad={this._onFeaturedImageLoaded}
                                        />
                                    }
                                </section>
                            </Fragment>
                        )
                    }
                    {
                        (isLookBookParkExperiment) && (
                            <section className="Home-container">
                                <LookBookPark />
                            </section>
                        )
                    }
                    {(isLoaded && Boolean(promoTypes.length)) &&
                        <section className="Home-container Home-promos-container">
                            {promoTypes.map(({orientation, promos}) => (
                                <div
                                    className={`Home-cards Home-cards-${orientation}`}
                                    key={orientation}
                                >
                                    {!isEmpty(promos.title) && (
                                        <h1 className="Home-heading">{promos.title}</h1>
                                    )}
                                    {
                                        promos.cards.map((card, index) => (
                                            <Card
                                                key={index}
                                                noPadding
                                            >
                                                <Promo
                                                    orientation={orientation}
                                                    imageId={`homepage/${card.imageId}`}
                                                    imagePosition={card.imagePosition}
                                                    title={card.title}
                                                    content={card.description}
                                                    buttonText={card.buttonText}
                                                    buttonLink={card.url}
                                                />
                                            </Card>
                                        ))
                                    }
                                </div>
                            ))}
                        </section>
                    }
                    <section className="Home-container Home-apps-container">
                        <HomeAppPromo
                            email={email}
                        />
                    </section>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        user: {
            admin: isAdmin,
            data: {
                email,
            }
        },
        search: {
            data: {
                referrer,
                referrerAffiliate
            }
        }
    } = state;

    return {
        email,
        isAdmin,
        referrer,
        referrerAffiliate
    };
};

export default connect(mapStateToProps)(Home);
